// components/Survey/surveyUI.jsx — small presentational pieces shared by the survey pages
import React, { useState, useCallback, useRef } from "react";
import {
  Button, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Spinner, Text,
} from "@fluentui/react-components";
import { CheckmarkCircle20Filled, ErrorCircle20Regular, Info20Regular } from "@fluentui/react-icons";
import { STATUS_META } from "./surveyUtils";

export const C = {
  navy: "#001A3D",
  navy2: "#002D7A",
  brand: "#0052CC",
  accent: "#0066FF",
  ink: "#201F1E",
  ink2: "#605E5C",
  muted: "#8A8886",
  line: "#EDEBE9",
  soft: "#F8F9FC",
  page: "#F3F2F1",
  danger: "#D13438",
};

export const card = {
  background: "#fff",
  border: `1px solid ${C.line}`,
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,26,61,0.04)",
};

export function StatusChip({ status, label }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: m.bg, color: m.color, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.color }} />
      {label || m.label}
    </span>
  );
}

export function KpiTile({ icon: Icon, label, value, sub, color = C.brand, children }) {
  return (
    <div style={{ ...card, padding: "16px 18px", flex: 1, minWidth: 170, display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {Icon && (
          <span style={{ width: 30, height: 30, borderRadius: 8, background: `${color}14`, color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Icon style={{ fontSize: 18 }} />
          </span>
        )}
        <Text style={{ fontSize: 12, color: C.ink2, fontWeight: 600 }}>{label}</Text>
      </div>
      <Text style={{ fontSize: 26, fontWeight: 800, color: C.ink, lineHeight: 1.1 }}>{value}</Text>
      {sub && <Text style={{ fontSize: 12, color: C.muted }}>{sub}</Text>}
      {children}
    </div>
  );
}

export function Meter({ pct, color = C.brand, height = 6 }) {
  const w = Math.max(0, Math.min(100, pct || 0));
  return (
    <div style={{ height, background: "#EEF1F6", borderRadius: height, overflow: "hidden" }}>
      <div style={{ width: `${w}%`, height: "100%", background: color, borderRadius: height, transition: "width 0.4s ease" }} />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div style={{ ...card, padding: "56px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      {Icon && (
        <span style={{ width: 64, height: 64, borderRadius: 18, background: "#EEF4FF", color: C.brand, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 6 }}>
          <Icon style={{ fontSize: 30 }} />
        </span>
      )}
      <Text style={{ fontSize: 17, fontWeight: 700, color: C.ink }}>{title}</Text>
      {text && <Text style={{ fontSize: 13, color: C.ink2, maxWidth: 440, lineHeight: 1.6 }}>{text}</Text>}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}

export function LoadingBlock({ label = "Loading surveys…" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
      <Spinner label={label} />
    </div>
  );
}

export function ErrorBanner({ error, onRetry }) {
  if (!error) return null;
  return (
    <div style={{ background: "#FDE7E9", border: "1px solid #F1BBBC", color: "#A4262C", borderRadius: 10, padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 16 }}>
      <ErrorCircle20Regular style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.6 }}>
        <strong>Could not reach survey storage.</strong> {error}
        <div style={{ color: "#7A2A2E", marginTop: 2 }}>
          Check that the SharePoint lists <code>SCMSurveys</code>, <code>SCMSurveyAssignments</code> and <code>SCMSurveyResponses</code> exist and that your account has access to them.
        </div>
      </div>
      {onRetry && <Button size="small" onClick={onRetry}>Retry</Button>}
    </div>
  );
}

export function SectionTitle({ children, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "4px 0 12px" }}>
      <Text style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{children}</Text>
      {right}
    </div>
  );
}

// ── Toast ──────────────────────────────────────────────────────────────────────

export function useToast() {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  const show = useCallback((msg, type = "success") => {
    clearTimeout(timer.current);
    setToast({ msg, type });
    timer.current = setTimeout(() => setToast(null), 4500);
  }, []);
  const node = toast ? (
    <div role="status" style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 2000, maxWidth: 420,
      background: toast.type === "error" ? "#A4262C" : toast.type === "info" ? C.navy2 : "#0B6A0B",
      color: "#fff", borderRadius: 10, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,0.22)",
      display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, lineHeight: 1.5,
    }}>
      {toast.type === "error" ? <ErrorCircle20Regular /> : toast.type === "info" ? <Info20Regular /> : <CheckmarkCircle20Filled />}
      <span>{toast.msg}</span>
    </div>
  ) : null;
  return [node, show];
}

// ── Confirm dialog ─────────────────────────────────────────────────────────────

export function ConfirmDialog({ open, title, children, confirmLabel = "Confirm", danger, busy, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && !busy && onCancel()}>
      <DialogSurface style={{ maxWidth: 460 }}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent style={{ fontSize: 14, color: C.ink2, lineHeight: 1.6 }}>{children}</DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button
              appearance="primary"
              onClick={onConfirm}
              disabled={busy}
              icon={busy ? <Spinner size="tiny" /> : undefined}
              style={danger ? { background: C.danger } : undefined}
            >
              {confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
